import { Injectable } from '@angular/core';
import { IFiDialogResult } from '../../../fi-dialogs/fi-dialogs';
import { FiItemsListSaveFilterDialogComponent } from '../../../fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-save-filter-dialog/fi-items-list-save-filter-dialog.component';
import {
    IChecklist2ForReport,
    IChecklist2ReportAnswer,
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportChecklistUnits,
    IChecklistV2ReportParameter,
} from '../shared/fi-checklist2-report';
import { FiDeviationChecklistDetailsTableWrapperDialogComponent } from '../components/deviations-report/deviation-checklist-details-table-wrapper-dialog/deviation-checklist-details-table-wrapper-dialog.component';
import { IAccessRightFilterCollection } from '../../../models';
import { ITask } from '../../../fi-task-shared/fi-task-shared';
import { FiDeviationOneTimeTaskEditorDialogComponent } from '../components/deviations-report/deviation-one-time-task-editor-dialog/deviation-one-time-task-editor-dialog.component';
import {
    FiDeviationTasksDialogComponent,
    IDeviationTasksDialogData,
} from '../components/deviations-report/deviation-tasks-dialog/deviation-tasks-dialog.component';
import {
    FiChecklistUnitAnswerDetailsDialogComponent,
    IChecklistUnitAnswerDetailsDialogData,
} from '../components/open-checklists-report/checklist-unit-answers-details-dialog/checklist-unit-answers-details-dialog.component';
import { FiChecklistAttachmentDetailsDialogComponent } from '../components/shared/checklist-attachment-details-dialog/checklist-attachment-details-dialog.component';
import { FiChecklist2ReportNewWidgetDialogComponent } from '../components/new-widget-dialog/fi-checklist2-report-new-widget-dialog.component';
import { FiDialogsService } from '../../../fi-dialogs';
import { AppTextService } from '../../../app-text';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { IChecklist2ReportViewData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';
import { FiReportWidgetDialogsService } from '@fi-shared/fi-report-widget/fi-report-widget-dialogs.service';
import { FiChecklist2ReportHelperService } from './fi-checklist2-report-helper.service';
import { FiChecklist2ReportType } from '../enums/fi-checklist2-report-type';
import { Subject } from 'rxjs';
import { INewWidgetDialogResult } from '@fi-shared/fi-report-widget/new-report-widget-dialog/new-widget-dialog-base';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';

@Injectable()
export class FiChecklist2ReportDialogsService {
    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiReportWidgetDialogsService: FiReportWidgetDialogsService,
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
    ) {}

    private _saveReportWidget$ = new Subject<IChecklistV2ReportParameter>();
    saveReportWidget$ = this._saveReportWidget$.asObservable();

    private _addReportWidgetDialogClosed$ = new Subject<void>();
    addReportWidgetDialogClosed$ = this._addReportWidgetDialogClosed$.asObservable();

    private newWidgetReportData: IChecklist2ReportViewData;
    getNewWidgetReportData(): IChecklist2ReportViewData {
        return this.newWidgetReportData;
    }

    showOpenChecklistsDeviationsListDialog(
        notOkDetails: IChecklist2ReportAnswer[],
        availableChecklists: IChecklist2ForReport[],
    ): IFiDialogResult<void> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiDeviationChecklistDetailsTableWrapperDialogComponent,
            contentComponentData: {
                answerDetails: notOkDetails,
                availableChecklists,
            },
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-report-open-checklists-deviations-list-wrapper-dialog-pane',
                ],
                width: '80%',
                minWidth: '900px',
                maxWidth: 'unset',
                autoFocus: false,
            },
        });
    }

    showDeviationOneTimeTaskDialog(
        checkpointAnswerId: number,
        accessRightFilterCollection: IAccessRightFilterCollection,
        isTaskV2: boolean,
        task?: ITaskV2,
        userId?: number,
    ): IFiDialogResult<ITaskV2> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiDeviationOneTimeTaskEditorDialogComponent,
            contentComponentData: {
                checkpointAnswerId: checkpointAnswerId,
                accessRightFilterCollection: accessRightFilterCollection,
                isTaskV2,
                task,
                userId,
            },
            isHeaderDividerShown: true,
            headerDividerClass: 'BrandedBorderColor',
            headerText: this.appTextService.get('checklist.checkpoint_action_one_time_task'),
            hideDialogTopBorder: true,
            yesButtonText: this.appTextService.get('task.save_task'),
            noButtonText: this.appTextService.get('common.cancel'),
            preventUppercaseButtonLabel: true,

            config: {
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-deviation-one-time-task-editor-dialog-dialog-pane',
                    'fi-rounded-dialog-full-round-buttons',
                ],
                width: '80%',
                maxWidth: '850px',
                autoFocus: false,
                closeOnNavigation: false,
            },
        });
    }

    openDeviationTasksDialog(dialogData: IDeviationTasksDialogData) {
        return this.fiDialogsService.custom({
            hideFooter: true,
            hideDialogTopBorder: true,
            hideHeader: true,
            isNewButtonsUI: true,
            contentComponentRef: FiDeviationTasksDialogComponent,
            contentComponentData: dialogData,
            preventUppercaseButtonLabel: true,
            config: {
                autoFocus: false,
                maxWidth: '770px',
                width: '80%',
                panelClass: ['fi-rounded-dialog-panel', 'fi-deviation-tasks-dialog-panel'],
            },
        });
    }

    showConfirmDeleteOneTimeTask() {
        return this.fiDialogsService.confirm({
            colorClass: 'red',
            headerText: this.appTextService.get('checklist.report_delete_one_time_task'),
            headerIconClass: 'fa-light fa-trash red',
            isTopCloseButtonShown: true,
            messageText: this.appTextService.get('checklist.report_delete_one_time_task_message'),
            yesButtonText: this.appTextService.get('common.delete'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
            },
        });
    }

    showConfirmArchiveOneTimeTask() {
        return this.fiDialogsService.confirm({
            colorClass: 'orange',
            isBigSizeTopBorder: true,
            headerText: this.appTextService.get('task.archive_one_time_task'),
            headerIconClass: 'far fa-circle-exclamation orange',
            isTopCloseButtonShown: true,
            messageText: this.appTextService.get('task.archive_one_time_task_description'),
            yesButtonText: this.appTextService.get('common.archive'),
            noButtonText: this.appTextService.get('common.cancel'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        });
    }

    showChecklistUnitAnswerDetailsDialog(dialogData: IChecklistUnitAnswerDetailsDialogData) {
        return this.fiDialogsService.custom({
            hideFooter: true,
            hideHeader: true,
            hideDialogTopBorder: true,
            contentComponentRef: FiChecklistUnitAnswerDetailsDialogComponent,
            contentComponentData: dialogData,
            preventUppercaseButtonLabel: true,
            config: {
                disableClose: true,
                autoFocus: false,
                width: '90%',
                maxWidth: '90%',
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'open-checklist-details-checklist-unit-answer-details-dialog-panel',
                ],
            },
        });
    }

    showChecklistAttachmentDetailsDialog(
        answerDetails: IChecklist2ReportAnswer,
        mediaBankFileReference: IMediaBankFileReference,
    ) {
        this.fiDialogsService.custom({
            hideFooter: true,
            hideHeader: true,
            hideDialogTopBorder: true,
            contentComponentRef: FiChecklistAttachmentDetailsDialogComponent,
            contentComponentData: { answerDetails, mediaBankFileReference },
            preventUppercaseButtonLabel: true,
            config: {
                disableClose: true,
                autoFocus: false,
                width: '90%',
                maxWidth: '950px',
                panelClass: [
                    'fi-rounded-dialog-panel',
                ],
            },
        });
    }

    openAddChecklistReportWidgetDialog(dialogData: IChecklist2ReportViewData): void {
        this.newWidgetReportData = dialogData;

        this.fiReportWidgetDialogsService
            .getNewWidgetTypeSelectDialog(
                this.fiChecklist2ReportHelperService.getAvailableChecklistReportWidgetsData(dialogData.auditMode),
                true,
            )
            .onYesButtonClick((reportType: FiChecklist2ReportType) => {
                this.openWidgetSettingsDialog(reportType);
            })
            .afterClosed((result) => {
                if (result?.reason !== FiDialogCloseReason.YesButton) {
                    this._addReportWidgetDialogClosed$.next();
                }
            });
    }

    private openWidgetSettingsDialog(
        reportType: FiChecklist2ReportType,
    ): IFiDialogResult<INewWidgetDialogResult<IChecklistV2ReportParameter>> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiChecklist2ReportNewWidgetDialogComponent,
            contentComponentData: reportType,
            isTopCloseButtonShown: false,
            yesButtonText: this.appTextService.get('common.save_widget'),
            noButtonText: this.appTextService.get('common.cancel'),
            hideHeader: true,
            hideDialogTopBorder: true,
            preventUppercaseButtonLabel: true,
            revertHeightChanges: true,
            hideFooter: true,
            config: {
                autoFocus: false,
                minWidth: '500px',
                maxWidth: '90%',
                width: '1124px',
                panelClass: ['fi-checklist2-report-new-widget-dialog-pane', 'fi-rounded-dialog-panel'],
                closeOnNavigation: false,
                restoreFocus: false,
            },
        })
            .onYesButtonClick((result) => {
                if (result.openTypeSelectStep) {
                    this.openAddChecklistReportWidgetDialog(this.newWidgetReportData);
                    return;
                }
                this._saveReportWidget$.next(result.widget);
            })
            .afterClosed((result) => {
                if (!result?.value?.openTypeSelectStep) {
                    this._addReportWidgetDialogClosed$.next();
                }
            });
    }

    showConfirmSaveNewReportWidget(): IFiDialogResult {
        return this.fiDialogsService.confirm({
            isTopCloseButtonShown: true,
            headerText: this.appTextService.get('checklist.report_new_widget_save_dialog_header'),
            headerIconClass: 'BrandedColor fal fa-bookmark',
            messageText: this.appTextService.get('common.proceed_confirmation_question'),
            yesButtonText: this.appTextService.get('common.proceed_and_save'),
            noButtonText: this.appTextService.get('common.cancel'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-confirmation-dialog-small-heading', 'fi-rounded-dialog-panel'],
                autoFocus: false,
            },
        });
    }
}
